import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import { useAuth } from '../contexts/AuthContext';
import {
  FPP_DECK,
  actionSentences,
  applyClaim,
  assertUniqueMatches,
  cardById,
  dealMatch,
  identificationSentence,
  sentenceOptions,
  sharedSymbol,
  topCard,
  type FppSymbol,
  type MatchState,
} from '../lib/amarkoshaFpp';
import {
  MAX_ROOM_PLAYERS,
  RoomError,
  audioRetentionConsensus,
  claimRoomCard,
  closeRoom,
  createRoom,
  heartbeatRoom,
  joinRoom,
  parseRoomCode,
  roomShareUrl,
  setAudioRetentionChoice,
  setGuideOpen as publishGuideOpen,
  setMicrophoneReady,
  startRoom,
  watchRoom,
  type FppRoom,
  type RoomSyncState,
} from '../lib/amarkoshaFppRoom';
import {
  clearMatchAudio,
  isPortableWav,
  listMatchAudio,
  playAudioBlob,
  preferredAudioMimeType,
  saveClaimAudio,
  toPortableWav,
  unlockAudioPlayback,
  type ClaimAudio,
  type ClaimAudioMetadata,
} from '../lib/amarkoshaFppAudio';
import { FppPeerMesh } from '../lib/amarkoshaFppPeer';
import '../styles/AmarkoshaFppGame.css';

type Phase = 'lobby' | 'play' | 'win';
type GuideTab = 'rules' | 'frames' | 'online' | 'guide';
type NoticeKind = 'info' | 'success' | 'warn' | 'error';
type Notice = { id: number; kind: NoticeKind; text: string };
type PeerStatus = 'connecting' | 'connected' | 'disconnected' | 'failed';
type DeliveryProgress = {
  targetIds: string[];
  sentIds: string[];
  acknowledgedIds: string[];
};
type SpeechRec = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  abort: () => void;
};

function speechCtor(): (new () => SpeechRec) | null {
  const w = window as Window & {
    SpeechRecognition?: new () => SpeechRec;
    webkitSpeechRecognition?: new () => SpeechRec;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

function symbolStyle(index: number): CSSProperties {
  if (index === 0) {
    return { left: '34%', top: '34%', width: '32%', height: '32%' };
  }
  const angle = ((index - 1) / 8) * Math.PI * 2 - Math.PI / 2;
  const radius = 31;
  const size = index % 2 === 0 ? 27 : 23;
  const cx = 50 + radius * Math.cos(angle);
  const cy = 50 + radius * Math.sin(angle);
  return {
    left: `${cx - size / 2}%`,
    top: `${cy - size / 2}%`,
    width: `${size}%`,
    height: `${size}%`,
  };
}

function FppCardView({
  cardId,
  shake,
  frozen,
  disabled,
  onPick,
}: {
  cardId: number;
  shake?: boolean;
  frozen?: boolean;
  disabled?: boolean;
  onPick: (word: string) => void;
}) {
  const card = cardById(cardId);
  return (
    <div className={`fpp-card${shake ? ' shake' : ''}${frozen ? ' frozen' : ''}`}>
      {card.words.map((word, index) => {
        const meta = FPP_DECK.symbols[word];
        return (
          <button
            key={word}
            type="button"
            className="fpp-sym"
            style={symbolStyle(index)}
            disabled={frozen || disabled}
            onClick={() => onPick(word)}
            aria-label={word}
          >
            <img src={meta.symbol} alt={word} />
          </button>
        );
      })}
    </div>
  );
}

function AudioReview({
  clips,
  en,
  onClear,
}: {
  clips: ClaimAudio[];
  en: boolean;
  onClear: () => void;
}) {
  if (clips.length === 0) {
    return <p>{en ? 'No retained round audio is available on this device.' : 'इस उपकरण पर सुरक्षित दौर-ध्वनि उपलब्ध नहीं है।'}</p>;
  }
  return (
    <div className="fpp-review">
      <div className="fpp-row">
        <strong>{en ? 'Final audio review' : 'अंतिम ध्वनि समीक्षा'}</strong>
        <button type="button" className="fpp-ghost" onClick={onClear}>
          {en ? 'Delete saved audio' : 'सुरक्षित ध्वनि मिटाएँ'}
        </button>
      </div>
      {clips.map((clip, index) => (
        <div className="fpp-review-item" key={clip.id}>
          <button type="button" className="fpp-icon-btn" onClick={() => void playAudioBlob(clip.audio)} aria-label={en ? `Play round ${index + 1}` : `दौर ${index + 1} सुनें`}>
            ▶
          </button>
          <div>
            <strong>{index + 1}. {clip.playerName} · {clip.word}</strong>
            <div>{clip.sentence}</div>
            {clip.transcript && <small>{en ? 'Optional transcript' : 'वैकल्पिक लिप्यंतरण'}: {clip.transcript}</small>}
          </div>
        </div>
      ))}
    </div>
  );
}

function appendUniqueClip(current: ClaimAudio[], clip: ClaimAudio, keepAll: boolean): ClaimAudio[] {
  if (current.some((item) => item.id === clip.id)) return current;
  return keepAll ? [...current, clip] : [clip];
}

function RemoteAudioPlayer({
  name,
  stream,
  en,
}: {
  name: string;
  stream: MediaStream;
  en: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [needsPlay, setNeedsPlay] = useState(false);

  const enablePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    void audio.play()
      .then(() => setNeedsPlay(false))
      .catch(() => setNeedsPlay(true));
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    audio.srcObject = stream;
    void audio.play()
      .then(() => setNeedsPlay(false))
      .catch(() => setNeedsPlay(true));
    return () => { audio.srcObject = null; };
  }, [stream]);

  return (
    <div className="fpp-remote-audio">
      <span>🔊 {name}</span>
      <audio ref={audioRef} autoPlay playsInline controls />
      {needsPlay && (
        <button type="button" className="fpp-chip" onClick={enablePlayback}>
          {en ? 'Enable live audio' : 'प्रत्यक्ष ध्वनि चालू करें'}
        </button>
      )}
    </div>
  );
}

export default function AmarkoshaFppGame({
  onNavigate,
  onRequireSignIn,
}: {
  onNavigate?: (path: string) => void;
  onRequireSignIn?: () => void;
}) {
  const { locale } = useGlossary();
  const { user, authLoading } = useAuth();
  const en = locale === 'en';
  const [phase, setPhase] = useState<Phase>('lobby');
  const [playerCount, setPlayerCount] = useState(1);
  const [match, setMatch] = useState<MatchState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [frozenUntil, setFrozenUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [claim, setClaim] = useState<FppSymbol | null>(null);
  const [heard, setHeard] = useState('');
  const [frame, setFrame] = useState('');
  const [listening, setListening] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processingAudio, setProcessingAudio] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const [localRetainAudio, setLocalRetainAudio] = useState(false);
  const [localMatchId, setLocalMatchId] = useState('');
  const [reviewAudio, setReviewAudio] = useState<ClaimAudio[]>([]);
  const [sessionAudio, setSessionAudio] = useState<ClaimAudio[]>([]);
  const [deliveryProgress, setDeliveryProgress] = useState<Record<string, DeliveryProgress>>({});
  const [remoteStreams, setRemoteStreams] = useState<Record<string, MediaStream>>({});
  const [unplayedAudio, setUnplayedAudio] = useState<ClaimAudio[]>([]);
  const [peerStatuses, setPeerStatuses] = useState<Record<string, PeerStatus>>({});
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideTab, setGuideTab] = useState<GuideTab>('rules');
  const [guideQuery, setGuideQuery] = useState('');
  const [room, setRoom] = useState<FppRoom | null>(null);
  const [shareCode, setShareCode] = useState<string | null>(() => (
    typeof window === 'undefined'
      ? null
      : parseRoomCode(new URLSearchParams(window.location.search).get('room'))
  ));
  const [myId, setMyId] = useState<string | null>(null);
  const [joinInput, setJoinInput] = useState('');
  const [onlineError, setOnlineError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<Notice | null>(null);
  const [syncState, setSyncState] = useState<RoomSyncState>('live');
  const [peekId, setPeekId] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const noticeIdRef = useRef(0);
  const prevRoomRef = useRef<FppRoom | null>(null);
  const claimCenterRef = useRef<number | undefined>(undefined);
  const peerStatusRef = useRef<Record<string, PeerStatus>>({});
  const discardRecordingRef = useRef(false);
  const recRef = useRef<SpeechRec | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderStreamRef = useRef<MediaStream | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef(0);
  const recordingTimerRef = useRef<number | null>(null);
  const heardRef = useRef('');
  const activeClaimIdRef = useRef('');
  const remoteStreamsRef = useRef(new Map<string, MediaStream>());
  const receivedClaimIdsRef = useRef(new Set<string>());
  const receivingClaimIdsRef = useRef(new Set<string>());
  const peerMeshRef = useRef<FppPeerMesh | null>(null);
  const unsubRef = useRef<ReturnType<typeof watchRoom> | null>(null);
  const activeRoomCode = room?.code;
  const localeRef = useRef(en);
  const retentionRef = useRef(false);
  const matchSessionRef = useRef('');
  const requireSignInRef = useRef(onRequireSignIn);
  localeRef.current = en;
  retentionRef.current = room ? audioRetentionConsensus(room) === true : false;
  requireSignInRef.current = onRequireSignIn;

  useEffect(() => {
    try {
      assertUniqueMatches(FPP_DECK.cards);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Deck invariant failed');
    }
  }, []);

  useEffect(() => {
    if (frozenUntil <= Date.now()) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 120);
    return () => window.clearInterval(id);
  }, [frozenUntil]);

  const showToast = (text: string, kind: NoticeKind = 'info', durationMs = 3200) => {
    noticeIdRef.current += 1;
    const notice: Notice = { id: noticeIdRef.current, kind, text };
    setToast(notice);
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      setToast((current) => (current?.id === notice.id ? null : current));
      toastTimerRef.current = null;
    }, durationMs);
  };
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const onlineFail = (err: unknown) => {
    if (err instanceof RoomError) {
      setOnlineError(err.message);
      showToast(err.message, err.code === 'too_late' ? 'warn' : 'error', 4200);
      return;
    }
    const text = en
      ? 'Online rooms are unavailable. Use local play or sign in to the portal again.'
      : 'ऑनलाइन कक्ष उपलब्ध नहीं। स्थानीय खेलें या पोर्टल में पुनः साइन इन करें।';
    setOnlineError(text);
    showToast(text, 'error', 4200);
  };

  const persistShareCode = (code: string) => {
    setShareCode(code);
    try {
      window.sessionStorage.setItem('amarkosha-fpp-room', code);
    } catch {
      // Private mode can reject sessionStorage; the in-memory code is enough for this tab.
    }
  };

  const attachRoom = useCallback((code: string, playerId: string) => {
    setMyId(playerId);
    persistShareCode(code);
    const nextPath = `/products/tools/game?room=${code}`;
    if (window.location.pathname === '/products/tools/game') {
      window.history.replaceState({}, '', nextPath);
    } else {
      onNavigate?.(nextPath);
    }
    unsubRef.current?.();
    unsubRef.current = watchRoom(code, (next) => {
      if (!next) return;
      const previous = prevRoomRef.current;
      const english = localeRef.current;
      if (previous && previous.code === next.code) {
        const prevIds = new Set(previous.players.map((player) => player.id));
        next.players.forEach((player) => {
          if (!prevIds.has(player.id) && player.id !== myIdRef.current) {
            showToastRef.current(
              english ? `${player.name} joined the room.` : `${player.name} कक्ष में जुड़े।`,
              'success',
            );
          }
        });
        previous.players.forEach((player) => {
          if (!next.players.some((item) => item.id === player.id) && player.id !== myIdRef.current) {
            showToastRef.current(
              english ? `${player.name} left the room.` : `${player.name} कक्ष छोड़ गए।`,
              'warn',
            );
          }
        });
        if (previous.status === 'lobby' && next.status === 'play') {
          showToastRef.current(english ? 'Match started. Find the one shared symbol.' : 'खेल शुरू। एक समान चिह्न ढूँढें।', 'success');
        }
        const claim = next.lastClaim;
        if (
          claim
          && claim.playerId !== myIdRef.current
          && claim.atMs !== previous.lastClaim?.atMs
        ) {
          const winner = next.players.find((player) => player.id === claim.playerId)?.name ?? (english ? 'A teammate' : 'एक साथी');
          showToastRef.current(
            english ? `${winner} claimed ${claim.word}.` : `${winner} ने ${claim.word} का दावा किया।`,
            'success',
            2800,
          );
        }
      }
      prevRoomRef.current = next;
      setRoom(next);
      if (next.status === 'play') {
        const nextMatchId = `${next.code}-${next.createdAtMs}`;
        if (matchSessionRef.current !== nextMatchId) {
          matchSessionRef.current = nextMatchId;
          setSessionAudio([]);
          setDeliveryProgress({});
        }
        setPhase('play');
      }
      if (next.status === 'closed') {
        setPhase(next.players.some((p) => p.pile.length > 0) ? 'win' : 'lobby');
        if (previous?.status !== 'closed') {
          showToastRef.current(english ? 'This room has ended.' : 'यह कक्ष समाप्त हो गया।', 'warn');
        }
      }
    }, (state) => {
      setSyncState(state);
      if (state === 'reconnecting') {
        showToastRef.current(localeRef.current ? 'Reconnecting to the room…' : 'कक्ष से पुनः जुड़ रहे हैं…', 'warn', 2400);
      } else if (state === 'lost') {
        showToastRef.current(
          localeRef.current
            ? 'Room sync is lost. Keep this page open; it will retry.'
            : 'कक्ष-संपर्क टूट गया। पृष्ठ खुला रखें; पुनः प्रयास होगा।',
          'error',
          5200,
        );
      } else if (state === 'ended') {
        showToastRef.current(localeRef.current ? 'This room is no longer available.' : 'यह कक्ष अब उपलब्ध नहीं।', 'warn');
      }
    });
  }, [onNavigate]);
  const attachRoomRef = useRef(attachRoom);
  attachRoomRef.current = attachRoom;
  const shareCodeRef = useRef(shareCode);
  shareCodeRef.current = shareCode;
  const myIdRef = useRef(myId);
  myIdRef.current = myId;

  useEffect(() => {
    const code = parseRoomCode(new URLSearchParams(window.location.search).get('room'));
    if (!code || authLoading) return undefined;
    persistShareCode(code);
    if (!user) {
      setOnlineError(localeRef.current ? 'Sign in to the portal to join this room. You will join automatically after sign-in.' : 'इस कक्ष में जुड़ने के लिए पोर्टल में साइन इन करें। साइन इन के बाद आप स्वतः जुड़ जाएँगे।');
      requireSignInRef.current?.();
      return undefined;
    }
    if (shareCodeRef.current === code && myIdRef.current) return undefined;
    let cancelled = false;
    joinRoom(code)
      .then(({ playerId, room: joined }) => {
        if (cancelled) return;
        setRoom(joined);
        attachRoomRef.current(code, playerId);
      })
      .catch((err) => {
        if (!cancelled) onlineFail(err);
      });
    return () => {
      cancelled = true;
      unsubRef.current?.();
    };
  }, [authLoading, user?.uid]);

  useEffect(() => {
    if (!activeRoomCode || !myId) return undefined;
    void publishGuideOpen(activeRoomCode, myId, guideOpen).catch(() => undefined);
    return undefined;
  }, [guideOpen, activeRoomCode, myId]);

  useEffect(() => {
    if (!activeRoomCode || !myId) {
      peerMeshRef.current?.destroy();
      peerMeshRef.current = null;
      setPeerStatuses({});
      setRemoteStreams({});
      return undefined;
    }
    let mesh: FppPeerMesh;
    const acceptRemoteAudio = async (
      remoteId: string,
      metadata: ClaimAudioMetadata,
      recorded: Blob,
    ) => {
      if (receivedClaimIdsRef.current.has(metadata.id)) {
        mesh.acknowledgeAudio(remoteId, metadata.id);
        return;
      }
      if (receivingClaimIdsRef.current.has(metadata.id)) return;
      receivingClaimIdsRef.current.add(metadata.id);
      if (
        recorded.size < 256
        || recorded.size > 2 * 1024 * 1024
        || !(await isPortableWav(recorded))
      ) {
        receivingClaimIdsRef.current.delete(metadata.id);
        setAudioError(localeRef.current ? 'Received audio archive was not a valid portable WAV.' : 'प्राप्त ध्वनि-संग्रह मान्य पोर्टेबल WAV नहीं था।');
        return;
      }
      try {
        const saved: ClaimAudio = { ...metadata, audio: recorded };
        setSessionAudio((current) => appendUniqueClip(current, saved, true));
        if (retentionRef.current) {
          setReviewAudio((current) => appendUniqueClip(current, saved, true));
          await saveClaimAudio(saved);
        }
        receivedClaimIdsRef.current.add(saved.id);
        mesh.acknowledgeAudio(remoteId, saved.id);
      } finally {
        receivingClaimIdsRef.current.delete(metadata.id);
      }
    };

    mesh = new FppPeerMesh({
      roomCode: activeRoomCode,
      playerId: myId,
      onRemoteStream: (remoteId, stream) => {
        remoteStreamsRef.current.set(remoteId, stream);
        setRemoteStreams((current) => ({ ...current, [remoteId]: stream }));
      },
      onClaimStarted: () => undefined,
      onClaimCommitted: (remoteId, metadata) => {
        if (receivedClaimIdsRef.current.has(metadata.id)) {
          mesh.acknowledgeAudio(remoteId, metadata.id);
        }
      },
      onClaimCancelled: () => undefined,
      onAudioArchive: (remoteId, metadata, audio) => {
        void acceptRemoteAudio(remoteId, metadata, audio)
          .catch(() => {
            setAudioError(localeRef.current ? 'Received audio could not be stored for replay.' : 'प्राप्त ध्वनि पुनः चलाने हेतु सुरक्षित नहीं हो सकी।');
          });
      },
      onAudioSent: (claimId, remoteId) => {
        setDeliveryProgress((current) => {
          const progress = current[claimId] ?? { targetIds: [], sentIds: [], acknowledgedIds: [] };
          return {
            ...current,
            [claimId]: {
              ...progress,
              sentIds: progress.sentIds.includes(remoteId) ? progress.sentIds : [...progress.sentIds, remoteId],
            },
          };
        });
      },
      onAudioAcknowledged: (claimId, remoteId) => {
        setDeliveryProgress((current) => {
          const progress = current[claimId] ?? { targetIds: [], sentIds: [], acknowledgedIds: [] };
          return {
            ...current,
            [claimId]: {
              ...progress,
              acknowledgedIds: progress.acknowledgedIds.includes(remoteId)
                ? progress.acknowledgedIds
                : [...progress.acknowledgedIds, remoteId],
            },
          };
        });
      },
      onStatus: setPeerStatuses,
      onError: (message) => showToastRef.current(message, 'warn', 4000),
    });
    peerMeshRef.current = mesh;
    const localTrack = recorderStreamRef.current?.getAudioTracks()[0];
    if (localTrack?.readyState === 'live') void mesh.setLocalAudioTrack(localTrack);
    return () => {
      mesh.destroy();
      remoteStreamsRef.current.clear();
      setRemoteStreams({});
      if (peerMeshRef.current === mesh) peerMeshRef.current = null;
    };
  }, [activeRoomCode, myId]);

  useEffect(() => {
    if (!room || !myId || !peerMeshRef.current) return;
    peerMeshRef.current.syncPeers(room.players.map((player) => player.id));
  }, [room, myId]);

  useEffect(() => {
    const previous = peerStatusRef.current;
    Object.entries(peerStatuses).forEach(([remoteId, status]) => {
      const before = previous[remoteId];
      if (before === status) return;
      const name = room?.players.find((player) => player.id === remoteId)?.name
        ?? (localeRef.current ? 'Teammate' : 'साथी');
      if (status === 'connected' && before !== 'connected') {
        showToastRef.current(
          localeRef.current ? `Audio link with ${name} is ready.` : `${name} से ध्वनि-संपर्क तैयार है।`,
          'success',
        );
      } else if ((status === 'failed' || status === 'disconnected') && before === 'connected') {
        showToastRef.current(
          localeRef.current ? `Audio link with ${name} dropped. Reconnecting…` : `${name} से ध्वनि-संपर्क टूटा। पुनः जुड़ रहे हैं…`,
          'warn',
          4200,
        );
      }
    });
    peerStatusRef.current = peerStatuses;
  }, [peerStatuses, room]);

  useEffect(() => {
    const onOffline = () => showToastRef.current(
      localeRef.current ? 'This device is offline. Room sync will retry.' : 'यह उपकरण ऑफ़लाइन है। कक्ष-संपर्क पुनः प्रयास करेगा।',
      'error',
      5000,
    );
    const onOnline = () => showToastRef.current(
      localeRef.current ? 'Network is back. Syncing the room…' : 'जाल वापस आया। कक्ष जुड़ रहा है…',
      'info',
    );
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  useEffect(() => {
    if (
      !activeRoomCode
      || !myId
      || (room?.status !== 'lobby' && room?.status !== 'play')
    ) return undefined;
    const beat = () => {
      void heartbeatRoom(activeRoomCode, myId)
        .then((ok) => {
          if (!ok) setSyncState((current) => (current === 'live' ? 'reconnecting' : current));
        })
        .catch((error) => onlineFail(error));
    };
    beat();
    const intervalId = window.setInterval(beat, 30_000);
    return () => window.clearInterval(intervalId);
  }, [activeRoomCode, myId, room?.status]);

  useEffect(() => {
    if (!activeRoomCode || !myId) return undefined;
    const recover = () => {
      if (document.visibilityState === 'hidden') return;
      if (room?.status === 'lobby' || room?.status === 'play') {
        void heartbeatRoom(activeRoomCode, myId)
          .then((ok) => {
            if (!ok) setSyncState((current) => (current === 'live' ? 'reconnecting' : current));
          })
          .catch((error) => onlineFail(error));
      }
      const track = recorderStreamRef.current?.getAudioTracks()[0];
      if (track?.readyState === 'live') {
        void peerMeshRef.current?.setLocalAudioTrack(track);
      } else if (microphoneEnabled) {
        setMicrophoneEnabled(false);
        void setMicrophoneReady(activeRoomCode, myId, false).catch(() => undefined);
      }
    };
    window.addEventListener('pageshow', recover);
    document.addEventListener('visibilitychange', recover);
    return () => {
      window.removeEventListener('pageshow', recover);
      document.removeEventListener('visibilitychange', recover);
    };
  }, [activeRoomCode, myId, room?.status, microphoneEnabled]);

  useEffect(() => () => {
    recRef.current?.abort();
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    if (recordingTimerRef.current !== null) window.clearTimeout(recordingTimerRef.current);
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const frozen = frozenUntil > now;
  const hasSpeech = typeof window !== 'undefined' && !!speechCtor();
  const hasRecorder = typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;
  const mobileBrowser = typeof navigator !== 'undefined'
    && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const canTranscribeDuringRecording = hasSpeech && !mobileBrowser;
  const online = !!room && !!myId;
  const me = room?.players.find((p) => p.id === myId) ?? null;
  const peek = room?.players.find((p) => p.id === peekId) ?? null;
  const visibleCode = shareCode || room?.code || null;
  const shareUrl = visibleCode ? roomShareUrl(visibleCode) : '';
  const roomRetention = room ? audioRetentionConsensus(room) : null;
  const retentionEnabled = online ? roomRetention === true : localRetainAudio;
  const matchId = online && room ? `${room.code}-${room.createdAtMs}` : localMatchId;
  const expectedPeerCount = online ? Math.max(0, (room?.players.length ?? 1) - 1) : 0;
  const connectedPeerCount = Object.values(peerStatuses).filter((status) => status === 'connected').length;
  const allMicrophonesReady = room?.players.every((player) => player.microphoneReady) === true;
  const microphoneReady = !online || (microphoneEnabled && me?.microphoneReady === true);
  const audioReady = !online || (
    connectedPeerCount === expectedPeerCount
    && microphoneReady
    && allMicrophonesReady
  );
  const enoughOnlinePlayers = (room?.players.length ?? 0) >= 2;

  const startLocal = useCallback(() => {
    void unlockAudioPlayback().catch(() => undefined);
    const nextMatchId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setRoom(null);
    setMyId(null);
    setShareCode(null);
    try { window.sessionStorage.removeItem('amarkosha-fpp-room'); } catch { /* ignore */ }
    setMatch(dealMatch(playerCount));
    setLocalMatchId(nextMatchId);
    setReviewAudio([]);
    setSessionAudio([]);
    setDeliveryProgress({});
    setPhase('play');
    setClaim(null);
    setPeekId(null);
    setFrozenUntil(0);
  }, [playerCount]);

  const handleCreateRoom = async () => {
    void unlockAudioPlayback().catch(() => undefined);
    if (!user) {
      setOnlineError(en ? 'Sign in to the portal before creating an online room.' : 'ऑनलाइन कक्ष बनाने से पहले पोर्टल में साइन इन करें।');
      onRequireSignIn?.();
      return;
    }
    setBusy(true);
    setOnlineError(null);
    try {
      const created = await createRoom();
      persistShareCode(created.code);
      setRoom(created.room);
      attachRoom(created.code, created.playerId);
      showToast(en ? `Room ${created.code} created. Share the invite link.` : `कक्ष ${created.code} बन गया। आमंत्रण लिंक भेजें।`, 'success');
    } catch (err) {
      onlineFail(err);
    } finally {
      setBusy(false);
    }
  };

  const handleJoinRoom = async () => {
    void unlockAudioPlayback().catch(() => undefined);
    if (!user) {
      setOnlineError(en ? 'Sign in to the portal before joining an online room.' : 'ऑनलाइन कक्ष में जुड़ने से पहले पोर्टल में साइन इन करें।');
      onRequireSignIn?.();
      return;
    }
    const code = parseRoomCode(joinInput);
    if (!code) {
      setOnlineError(en ? 'Enter a 6-character room code.' : '६ अक्षर का कक्ष-कोड लिखें।');
      return;
    }
    setBusy(true);
    setOnlineError(null);
    try {
      const joined = await joinRoom(code);
      persistShareCode(joined.room.code);
      setRoom(joined.room);
      attachRoom(joined.room.code, joined.playerId);
      showToast(en ? `Joined room ${joined.room.code}.` : `कक्ष ${joined.room.code} में जुड़ गए।`, 'success');
    } catch (err) {
      onlineFail(err);
    } finally {
      setBusy(false);
    }
  };

  const handleStartOnline = async () => {
    void unlockAudioPlayback().catch(() => undefined);
    if (!room || !myId) return;
    if (room.players.length < 2) {
      setOnlineError(en ? 'A second player must open the room link and sign in with their own portal account.' : 'दूसरे खिलाड़ी को कक्ष-लिंक खोलकर अपने पोर्टल खाते से साइन इन करना होगा।');
      return;
    }
    if (roomRetention === null) {
      setOnlineError(en ? 'Every player must choose the same audio retention option before the match starts.' : 'खेल शुरू होने से पहले प्रत्येक खिलाड़ी को एक ही ध्वनि-संग्रह विकल्प चुनना होगा।');
      return;
    }
    if (!audioReady) {
      setOnlineError(en ? 'Wait for every audio link before starting the match.' : 'खेल शुरू करने से पहले सभी ध्वनि-संपर्क जुड़ने दें।');
      return;
    }
    setBusy(true);
    try {
      await startRoom(room.code, myId);
      showToast(en ? 'Starting the match…' : 'खेल शुरू हो रहा है…', 'info');
    } catch (err) {
      onlineFail(err);
    } finally {
      setBusy(false);
    }
  };

  const enableMicrophone = async () => {
    if (!room || !myId || !hasRecorder) return;
    setBusy(true);
    setAudioError(null);
    try {
      let stream = recorderStreamRef.current;
      let track = stream?.getAudioTracks()[0];
      if (!stream || !track || track.readyState !== 'live') {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        track = stream.getAudioTracks()[0];
      }
      if (!track) throw new Error('No microphone track was returned.');
      track.enabled = false;
      track.onended = () => {
        setMicrophoneEnabled(false);
        void setMicrophoneReady(room.code, myId, false).catch(() => undefined);
      };
      recorderStreamRef.current = stream;
      const mesh = peerMeshRef.current;
      if (!mesh) throw new Error('Audio links are not ready.');
      await mesh.setLocalAudioTrack(track);
      await setMicrophoneReady(room.code, myId, true);
      setMicrophoneEnabled(true);
      showToast(en ? 'Microphone is ready for live claims.' : 'माइक्रोफ़ोन प्रत्यक्ष दावों के लिए तैयार है।', 'success');
    } catch (error) {
      recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
      recorderStreamRef.current = null;
      setMicrophoneEnabled(false);
      const name = error instanceof DOMException ? error.name : '';
      setAudioError(name === 'NotAllowedError'
        ? (en ? 'Allow microphone access for raajadharma.org in browser settings.' : 'ब्राउज़र सेटिंग में raajadharma.org को माइक्रोफ़ोन अनुमति दें।')
        : (en ? 'The microphone could not be prepared for live audio.' : 'प्रत्यक्ष ध्वनि हेतु माइक्रोफ़ोन तैयार नहीं हो सका।'));
    } finally {
      setBusy(false);
    }
  };

  const releaseMicrophone = () => {
    recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
    recorderStreamRef.current = null;
    setMicrophoneEnabled(false);
    void peerMeshRef.current?.setLocalAudioTrack(null);
    if (room && myId) void setMicrophoneReady(room.code, myId, false).catch(() => undefined);
  };

  const chooseRoomRetention = async (retainAudio: boolean) => {
    if (!room || !myId) return;
    setBusy(true);
    setOnlineError(null);
    try {
      await setAudioRetentionChoice(room.code, myId, retainAudio);
      showToast(
        retainAudio
          ? (en ? 'You voted to retain winning audio on every phone.' : 'आपने प्रत्येक फ़ोन पर विजयी ध्वनि सुरक्षित रखने का मत दिया।')
          : (en ? 'You voted not to save winning audio.' : 'आपने विजयी ध्वनि सुरक्षित न रखने का मत दिया।'),
        'info',
      );
    } catch (err) {
      onlineFail(err);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showToast(en ? 'Invite link copied.' : 'आमंत्रण लिंक कॉपी हो गया।', 'success', 2000);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast(en ? 'Copy failed. Select the invite link and copy it by hand.' : 'कॉपी नहीं हुआ। आमंत्रण लिंक चुनकर स्वयं कॉपी करें।', 'warn', 5000);
    }
  };

  const leaveOnlineLobby = () => {
    releaseMicrophone();
    unsubRef.current?.();
    unsubRef.current = null;
    setRoom(null);
    setMyId(null);
    setShareCode(null);
    setOnlineError(null);
    setSyncState('live');
    prevRoomRef.current = null;
    try { window.sessionStorage.removeItem('amarkosha-fpp-room'); } catch { /* ignore */ }
    if (window.location.pathname === '/products/tools/game') {
      window.history.replaceState({}, '', '/products/tools/game');
    } else {
      onNavigate?.('/products/tools/game');
    }
  };

  const centerId = online
    ? topCard(room?.center ?? [])
    : (match ? topCard(match.center) : undefined);
  const handId = online
    ? topCard((peek ?? me)?.pile ?? [])
    : (match ? topCard(match.players[match.activePlayer]?.pile ?? []) : undefined);
  const myHandId = online
    ? topCard(me?.pile ?? [])
    : (match ? topCard(match.players[match.activePlayer]?.pile ?? []) : undefined);

  const expected = useMemo(() => {
    if (centerId === undefined || myHandId === undefined) return null;
    return sharedSymbol(cardById(myHandId).words, cardById(centerId).words);
  }, [centerId, myHandId]);

  const pickSymbol = (word: string) => {
    if (frozen || !expected || guideOpen) return;
    if (online && peek && peek.id !== myId) return;
    if (word !== expected) {
      setShake(true);
      setFrozenUntil(Date.now() + 1500);
      window.setTimeout(() => setShake(false), 450);
      showToast(
        en ? 'Not the shared symbol. Pause 1.5 seconds, then look again.' : 'यह समान चिह्न नहीं। १.५ सेकंड रुकें, फिर देखें।',
        'warn',
        1800,
      );
      try { navigator.vibrate?.(40); } catch { /* ignore */ }
      return;
    }
    const symbol = FPP_DECK.symbols[word];
    claimCenterRef.current = centerId;
    setClaim(symbol);
    setHeard('');
    heardRef.current = '';
    setAudioError(null);
    setFrame(identificationSentence(symbol.word, symbol.gender));
    try { navigator.vibrate?.(12); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!online || !claim) return;
    if (claimCenterRef.current === undefined || centerId === claimCenterRef.current) return;
    discardRecordingRef.current = true;
    if (recorderRef.current?.state === 'recording') {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    showToastRef.current(
      en ? 'The centre card changed. This claim was cancelled.' : 'केंद्र पत्र बदल गया। यह दावा रद्द हुआ।',
      'warn',
    );
    resetClaim();
  }, [centerId, claim, online, en]);

  const stopListen = () => {
    recRef.current?.abort();
    recRef.current = null;
    setListening(false);
  };

  const startListen = () => {
    if (!claim) return;
    const Ctor = speechCtor();
    if (!Ctor) return;
    stopListen();
    const rec = new Ctor();
    rec.lang = 'sa-IN';
    rec.interimResults = false;
    rec.maxAlternatives = 3;
    rec.onresult = (ev) => {
      const bits: string[] = [];
      for (let i = 0; i < ev.results.length; i += 1) {
        bits.push(ev.results[i][0]?.transcript ?? '');
      }
      const transcript = bits.join(' ').trim();
      heardRef.current = transcript;
      setHeard(transcript);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try {
      setListening(true);
      rec.start();
    } catch {
      recRef.current = null;
      setListening(false);
    }
  };

  const resetClaim = () => {
    if (online && activeClaimIdRef.current) {
      peerMeshRef.current?.cancelAudioClaim(activeClaimIdRef.current);
    }
    activeClaimIdRef.current = '';
    claimCenterRef.current = undefined;
    setClaim(null);
    setHeard('');
    heardRef.current = '';
    setFrame('');
    setAudioError(null);
    stopListen();
  };

  const saveAcceptedAudio = async (audio: ClaimAudio) => {
    if (!retentionEnabled) return;
    setReviewAudio((current) => (
      current.some((item) => item.id === audio.id) ? current : [...current, audio]
    ));
    try {
      await saveClaimAudio(audio);
    } catch {
      setAudioError(en ? 'This device could not retain the round audio.' : 'यह उपकरण दौर की ध्वनि सुरक्षित नहीं कर सका।');
    }
  };

  const submitRecordedClaim = async (
    claimId: string,
    symbol: FppSymbol,
    sentence: string,
    audio: Blob,
    durationMs: number,
  ) => {
    if (durationMs < 600 || audio.size < 256) {
      if (online) peerMeshRef.current?.cancelAudioClaim(claimId);
      activeClaimIdRef.current = '';
      setAudioError(en ? 'No clear audio was captured. Please record the word and sentence again.' : 'स्पष्ट ध्वनि नहीं मिली। शब्द और वाक्य पुनः बोलें।');
      return;
    }
    if (
      online
      && (
        !audioReady
        || peerMeshRef.current?.connectedPeerIds().length !== expectedPeerCount
        || peerMeshRef.current?.claimParticipantIds(claimId).length !== expectedPeerCount
      )
    ) {
      peerMeshRef.current?.cancelAudioClaim(claimId);
      activeClaimIdRef.current = '';
      setAudioError(en
        ? 'The audio link changed during this claim. Reconnect and record it again.'
        : 'इस दावे के दौरान ध्वनि-संपर्क बदल गया। पुनः जुड़कर दावा फिर रिकॉर्ड करें।');
      return;
    }

    let accepted = true;
    if (online && room && myId) {
      try {
        if (centerId === undefined) {
          peerMeshRef.current?.cancelAudioClaim(claimId);
          activeClaimIdRef.current = '';
          return;
        }
        const claimedCenter = claimCenterRef.current ?? centerId;
        if (claimedCenter === undefined) {
          peerMeshRef.current?.cancelAudioClaim(claimId);
          activeClaimIdRef.current = '';
          return;
        }
        const result = await claimRoomCard(room.code, myId, symbol.word, claimedCenter);
        if (result === 'too_late') {
          accepted = false;
          showToast(en ? 'Too late — the other audio claim won.' : 'देर हो गई — दूसरा ध्वनि-दावा पहले पहुँचा।', 'warn');
        }
      } catch (err) {
        accepted = false;
        if (err instanceof RoomError && err.code === 'mismatch') {
          setShake(true);
          setFrozenUntil(Date.now() + 1500);
        } else {
          onlineFail(err);
        }
      }
    } else if (match) {
      const next = applyClaim(match, match.activePlayer);
      setMatch(next);
      if (next.center.length === 0) setPhase('win');
    }
    if (!accepted) {
      if (online) peerMeshRef.current?.cancelAudioClaim(claimId);
      activeClaimIdRef.current = '';
      resetClaim();
      return;
    }

    const playerId = online ? myId ?? 'unknown' : `local-${match?.activePlayer ?? 0}`;
    const playerName = online
      ? me?.name ?? 'Player'
      : (en ? `Player ${(match?.activePlayer ?? 0) + 1}` : `खिलाड़ी ${(match?.activePlayer ?? 0) + 1}`);
    const roundId = `${centerId ?? 'end'}`;
    const acceptedAudio: ClaimAudio = {
      id: claimId,
      matchId,
      roundId,
      playerId,
      playerName,
      word: symbol.word,
      sentence,
      transcript: heardRef.current,
      mimeType: audio.type || 'audio/webm',
      durationMs,
      createdAtMs: recordingStartedAtRef.current,
      audio,
    };
    setSessionAudio((current) => appendUniqueClip(current, acceptedAudio, true));
    showToast(
      en ? `You claimed ${symbol.word}.` : `आपने ${symbol.word} का दावा किया।`,
      'success',
    );

    if (online) {
      const payload: ClaimAudioMetadata = {
        id: acceptedAudio.id,
        matchId: acceptedAudio.matchId,
        roundId: acceptedAudio.roundId,
        playerId: acceptedAudio.playerId,
        playerName: acceptedAudio.playerName,
        word: acceptedAudio.word,
        sentence: acceptedAudio.sentence,
        transcript: acceptedAudio.transcript,
        mimeType: acceptedAudio.mimeType,
        durationMs: acceptedAudio.durationMs,
        createdAtMs: acceptedAudio.createdAtMs,
      };
      const targeted = peerMeshRef.current?.commitAudioClaim(payload, audio) ?? [];
      setDeliveryProgress((current) => {
        const progress = current[acceptedAudio.id] ?? { targetIds: [], sentIds: [], acknowledgedIds: [] };
        return {
          ...current,
          [acceptedAudio.id]: { ...progress, targetIds: targeted },
        };
      });
      if (peerMeshRef.current?.connectedPeerIds().length !== expectedPeerCount) {
        setOnlineError(en ? 'The card was won, but audio delivery to one or more phones failed.' : 'पत्र जीत लिया गया, पर एक या अधिक फ़ोन तक ध्वनि नहीं पहुँची।');
      }
    }
    await saveAcceptedAudio(acceptedAudio);
    activeClaimIdRef.current = '';
    resetClaim();
  };

  const stopRecording = () => {
    if (recordingTimerRef.current !== null) {
      window.clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (recorderRef.current?.state === 'recording') {
      recorderStreamRef.current?.getAudioTracks().forEach((track) => { track.enabled = false; });
      try { recorderRef.current.requestData(); } catch { /* recorder will flush on stop */ }
      recorderRef.current.stop();
    }
  };

  const startRecording = async () => {
    if (!claim || !frame || recording || processingAudio || !hasRecorder) return;
    setAudioError(null);
    setHeard('');
    heardRef.current = '';
    const activeClaim = claim;
    const activeFrame = frame;
    const playerId = online ? myId ?? 'unknown' : `local-${match?.activePlayer ?? 0}`;
    const playerName = online
      ? me?.name ?? 'Player'
      : (en ? `Player ${(match?.activePlayer ?? 0) + 1}` : `खिलाड़ी ${(match?.activePlayer ?? 0) + 1}`);
    const roundId = `${centerId ?? 'pending'}`;
    const activeClaimId = `${matchId}-${roundId}-${playerId}`;
    activeClaimIdRef.current = activeClaimId;
    try {
      const existingStream = online ? recorderStreamRef.current : null;
      const existingTrack = existingStream?.getAudioTracks()[0];
      const stream = existingStream && existingTrack?.readyState === 'live'
        ? existingStream
        : await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
      const mimeType = preferredAudioMimeType();
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, {
          ...(mimeType ? { mimeType } : {}),
          audioBitsPerSecond: 24_000,
        });
      } catch {
        recorder = new MediaRecorder(stream);
      }
      recorderStreamRef.current = stream;
      recorderRef.current = recorder;
      recorderChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) recorderChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
        recorderStreamRef.current = null;
        setMicrophoneEnabled(false);
        if (online && room && myId) void setMicrophoneReady(room.code, myId, false).catch(() => undefined);
        recorderRef.current = null;
        setRecording(false);
        setProcessingAudio(false);
        setAudioError(en ? 'Android could not capture the microphone. Close other voice apps, check Chrome microphone permission, and try again.' : 'Android माइक्रोफ़ोन रिकॉर्ड नहीं कर सका। अन्य ध्वनि ऐप बंद करें, Chrome की माइक्रोफ़ोन अनुमति जाँचें और पुनः प्रयास करें।');
      };
      recorder.onstop = () => {
        const durationMs = Date.now() - recordingStartedAtRef.current;
        const recorded = new Blob(recorderChunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        recorderChunksRef.current = [];
        if (!online) {
          recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
          recorderStreamRef.current = null;
        }
        recorderRef.current = null;
        setRecording(false);
        stopListen();
        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          setProcessingAudio(false);
          return;
        }
        setProcessingAudio(true);
        void toPortableWav(recorded)
          .catch((error) => {
            if (online) throw error;
            return recorded;
          })
          .then(async (audio) => {
            if (online && !(await isPortableWav(audio))) {
              throw new Error('Online audio could not be converted to portable WAV.');
            }
            return audio;
          })
          .then((audio) => submitRecordedClaim(activeClaimId, activeClaim, activeFrame, audio, durationMs))
          .catch(() => {
            if (online) peerMeshRef.current?.cancelAudioClaim(activeClaimId);
            activeClaimIdRef.current = '';
            setAudioError(en
              ? 'This recording could not be converted for iPhone and Android playback. Please record it again.'
              : 'यह रिकॉर्डिंग iPhone और Android पर चलने योग्य नहीं बन सकी। कृपया पुनः रिकॉर्ड करें।');
          })
          .finally(() => setProcessingAudio(false));
      };
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) throw new Error('No microphone track was returned.');
      audioTrack.enabled = false;
      if (online) {
        const mesh = peerMeshRef.current;
        if (!mesh) throw new Error('Audio links are not ready.');
        await mesh.setLocalAudioTrack(audioTrack);
      }
      if (online && !microphoneEnabled) {
        if (room && myId) await setMicrophoneReady(room.code, myId, true);
        setMicrophoneEnabled(true);
      }
      recordingStartedAtRef.current = Date.now();
      recorder.start(250);
      if (online) {
        const targets = peerMeshRef.current?.beginAudioClaim({
          id: activeClaimId,
          matchId,
          roundId,
          playerId,
          playerName,
          word: activeClaim.word,
          sentence: activeFrame,
          transcript: '',
          mimeType: 'audio/wav',
          durationMs: 0,
          createdAtMs: recordingStartedAtRef.current,
        }) ?? [];
        setDeliveryProgress((current) => ({
          ...current,
          [activeClaimId]: { targetIds: targets, sentIds: [], acknowledgedIds: [] },
        }));
      }
      audioTrack.enabled = true;
      setRecording(true);
      if (canTranscribeDuringRecording) startListen();
      recordingTimerRef.current = window.setTimeout(stopRecording, 8_000);
    } catch (error) {
      if (online && activeClaimIdRef.current) {
        peerMeshRef.current?.cancelAudioClaim(activeClaimIdRef.current);
      }
      activeClaimIdRef.current = '';
      if (!online) {
        recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
        recorderStreamRef.current = null;
      }
      recorderRef.current = null;
      setRecording(false);
      const name = error instanceof DOMException ? error.name : '';
      const message = name === 'NotAllowedError'
        ? (en ? 'Allow microphone access for raajadharma.org in Chrome site settings.' : 'Chrome साइट सेटिंग में raajadharma.org को माइक्रोफ़ोन अनुमति दें।')
        : name === 'NotFoundError'
          ? (en ? 'No microphone was found on this device.' : 'इस उपकरण पर माइक्रोफ़ोन नहीं मिला।')
          : (en ? 'The microphone is busy or unavailable. Close other voice apps and try again.' : 'माइक्रोफ़ोन व्यस्त या अनुपलब्ध है। अन्य ध्वनि ऐप बंद करके पुनः प्रयास करें।');
      setAudioError(message);
    }
  };

  const ranked = online
    ? [...(room?.players ?? [])].sort((a, b) => b.pile.length - a.pile.length)
    : (match ? [...match.players].sort((a, b) => b.pile.length - a.pile.length) : []);

  const guideSymbols = useMemo(() => {
    const q = guideQuery.trim().toLowerCase();
    return Object.values(FPP_DECK.symbols).filter((item) => {
      if (!q) return true;
      return [item.word, item.hindi, item.dhatu, item.nirukta].some((field) =>
        field.toLowerCase().includes(q),
      );
    });
  }, [guideQuery]);

  useEffect(() => {
    if (!retentionEnabled || !matchId || (phase !== 'play' && phase !== 'win')) return undefined;
    let cancelled = false;
    void listMatchAudio(matchId)
      .then((saved) => {
        if (cancelled) return;
        const mergeSaved = (current: ClaimAudio[]) => {
          const merged = new Map(current.map((item) => [item.id, item]));
          saved.forEach((item) => merged.set(item.id, item));
          return [...merged.values()].sort((a, b) => a.createdAtMs - b.createdAtMs);
        };
        setReviewAudio(mergeSaved);
        setSessionAudio(mergeSaved);
      })
      .catch(() => setAudioError(en ? 'Saved audio could not be loaded for review.' : 'समीक्षा के लिए सुरक्षित ध्वनि नहीं खोली जा सकी।'));
    return () => { cancelled = true; };
  }, [phase, retentionEnabled, matchId, en]);

  const clearReviewAudio = async () => {
    if (!matchId) return;
    try {
      await clearMatchAudio(matchId);
      setReviewAudio([]);
      setSessionAudio((current) => current.filter((clip) => clip.matchId !== matchId));
    } catch {
      setAudioError(en ? 'Saved audio could not be deleted.' : 'सुरक्षित ध्वनि मिटाई नहीं जा सकी।');
    }
  };

  const playUnplayedAudio = async (clip: ClaimAudio) => {
    try {
      await playAudioBlob(clip.audio);
      setUnplayedAudio((current) => current.filter((item) => item.id !== clip.id));
    } catch {
      setAudioError(en ? `This device cannot play ${clip.mimeType || 'the received audio format'}.` : `यह उपकरण ${clip.mimeType || 'प्राप्त ध्वनि प्रारूप'} नहीं चला सकता।`);
    }
  };

  if (loadError) {
    return <div className="fpp-root"><p>{loadError}</p></div>;
  }

  const inLobby = phase === 'lobby' || (online && room?.status === 'lobby');
  const inPlay = phase === 'play' && centerId !== undefined && handId !== undefined;
  const peeking = !!(online && peek && peek.id !== myId);
  const deliveryLabel = (clip: ClaimAudio): string => {
    if (!online) return en ? 'Recorded on this device' : 'इस उपकरण पर रिकॉर्ड हुई';
    if (clip.playerId !== myId) return en ? 'Received on this device' : 'इस उपकरण पर प्राप्त हुई';
    const progress = deliveryProgress[clip.id];
    const targetCount = progress?.targetIds.length ?? expectedPeerCount;
    const sentCount = progress?.sentIds.length ?? 0;
    const acknowledgedCount = progress?.acknowledgedIds.length ?? 0;
    if (targetCount > 0 && acknowledgedCount === targetCount) {
      return en ? `Received by ${acknowledgedCount}/${targetCount} teammates` : `${acknowledgedCount}/${targetCount} साथियों के उपकरण पर प्राप्त`;
    }
    if (sentCount > 0) {
      return en ? `Sent to ${sentCount}/${targetCount}; awaiting receipt` : `${sentCount}/${targetCount} को भेजी; प्राप्ति की प्रतीक्षा`;
    }
    return en ? `Queued for ${targetCount} teammate device${targetCount === 1 ? '' : 's'}` : `${targetCount} साथी उपकरण हेतु पंक्तिबद्ध`;
  };

  return (
    <div className="fpp-root">
      <div className="fpp-hud">
        <strong className="fpp-title">{en ? 'One in Nine' : 'एक में नौ'}</strong>
        {(match || (room && room.status !== 'lobby')) && (
          <span className="fpp-score">
            {en ? 'Centre' : 'केंद्र'}: {online ? room?.center.length ?? 0 : match?.center.length ?? 0}
          </span>
        )}
        {online && room?.players.map((player) => (
          <button
            key={player.id}
            type="button"
            className={`fpp-chip${player.id === (peekId ?? myId) ? ' active' : ''}${player.guideOpen ? ' guide' : ''}`}
            title={player.guideOpen ? (en ? 'Consulting guide' : 'मार्गदर्शिका देख रहे हैं') : player.name}
            onClick={() => setPeekId(player.id === myId ? null : player.id)}
          >
            {player.name} · {player.pile.length}
            {player.retainAudio === true ? ' · 💾' : player.retainAudio === false ? ' · ∅' : ''}
            {player.microphoneReady ? ' · 🎙️' : ''}
            {player.id !== myId && peerStatuses[player.id] === 'connected' ? ' · 🔊' : ''}
            {player.guideOpen ? ' 📖' : ''}
          </button>
        ))}
        {!online && match && match.players.map((player, index) => (
          <button
            key={player.id}
            type="button"
            className={`fpp-chip${index === match.activePlayer ? ' active' : ''}`}
            onClick={() => setMatch({ ...match, activePlayer: index })}
          >
            {en ? `P${player.id}` : `खिलाड़ी ${player.id}`} · {player.pile.length}
          </button>
        ))}
        <button type="button" className="fpp-icon-btn" onClick={() => setGuideOpen(true)} aria-label={en ? 'Player guide' : 'मार्गदर्शिका'}>?</button>
        <button type="button" className="fpp-ghost" onClick={() => onNavigate?.('/products')}>{en ? 'Tools' : 'उपकरण'}</button>
      </div>
      {online && (
        <div
          className={`fpp-sync fpp-sync-${syncState}`}
          role="status"
          aria-live="polite"
        >
          {syncState === 'live'
            ? (en ? 'Room sync live' : 'कक्ष-संपर्क चालू')
            : syncState === 'reconnecting'
              ? (en ? 'Reconnecting to the room…' : 'कक्ष से पुनः जुड़ रहे हैं…')
              : syncState === 'lost'
                ? (en ? 'Room sync lost — retrying' : 'कक्ष-संपर्क टूटा — पुनः प्रयास')
                : (en ? 'This room has ended' : 'यह कक्ष समाप्त हो गया')}
          {enoughOnlinePlayers && (
            <span>
              {en
                ? ` · Audio ${connectedPeerCount}/${expectedPeerCount}`
                : ` · ध्वनि ${connectedPeerCount}/${expectedPeerCount}`}
            </span>
          )}
        </div>
      )}

      {inLobby && (
        <div className="fpp-lobby">
          <p>
            {en
              ? 'One in Nine: any two cards share exactly one symbol. Tap it, say the Sanskrit word, then speak a sentence. Create a room to play on separate phones.'
              : 'एक में नौ: किन्हीं दो पत्रों पर ठीक एक ही चिह्न समान होता है। उसे छुएँ, संस्कृत शब्द बोलें, फिर वाक्य बोलें। अलग-अलग फ़ोन पर खेलने के लिए कक्ष बनाएँ।'}
          </p>
          {onlineError && (
            <div className="fpp-banner" role="alert">
              <span>{onlineError}</span>
              <button type="button" className="fpp-ghost" onClick={() => setOnlineError(null)}>
                {en ? 'Dismiss' : 'हटाएँ'}
              </button>
            </div>
          )}
          {(room || shareCode) && (
            <>
              <p>{en ? `Room ${visibleCode} · ${room?.players.length ?? 1}/${MAX_ROOM_PLAYERS} joined` : `कक्ष ${visibleCode} · ${room?.players.length ?? 1}/${MAX_ROOM_PLAYERS} जुड़े`}</p>
              {!enoughOnlinePlayers && (
                <div className="fpp-banner fpp-banner-info">
                  {en
                    ? 'Waiting for player 2. Send this link to the other player; they must open it and sign in with their own portal account. If both players share this phone, use Local players: 2 instead.'
                    : 'खिलाड़ी २ की प्रतीक्षा है। यह लिंक दूसरे खिलाड़ी को भेजें; उन्हें इसे खोलकर अपने अलग पोर्टल खाते से साइन इन करना होगा। यदि दोनों इसी फ़ोन पर खेलेंगे, तो स्थानीय खिलाड़ी: २ चुनें।'}
                </div>
              )}
              <label className="fpp-link-label" htmlFor="fpp-share-url">{en ? 'Invite link' : 'आमंत्रण लिंक'}</label>
              <div className="fpp-link-row">
                <input
                  id="fpp-share-url"
                  className="fpp-link"
                  readOnly
                  value={shareUrl}
                  onFocus={(event) => event.currentTarget.select()}
                />
                <button type="button" className="fpp-chip" onClick={() => void copyLink()}>
                  {copied ? (en ? 'Copied' : 'कॉपी') : (en ? 'Copy link' : 'लिंक कॉपी')}
                </button>
              </div>
              {!room && shareCode && (
                <p>{en ? 'Room created. Keep this page open and share the link above.' : 'कक्ष बन गया। यह पृष्ठ खुला रखें और ऊपर का लिंक साझा करें।'}</p>
              )}
              {room && (
              <>
              <fieldset className="fpp-consent">
                <legend>{en ? 'Should every phone retain all winning audio for final review?' : 'क्या प्रत्येक फ़ोन अंतिम समीक्षा हेतु सभी विजयी ध्वनियाँ सुरक्षित रखे?'}</legend>
                <p>
                  {en
                    ? 'Audio stays on each player’s device and is never uploaded. Everyone must make the same choice.'
                    : 'ध्वनि प्रत्येक खिलाड़ी के उपकरण पर ही रहेगी और अपलोड नहीं होगी। सभी को एक ही विकल्प चुनना होगा।'}
                </p>
                <div className="fpp-row">
                  <button
                    type="button"
                    className={`fpp-chip${me?.retainAudio === true ? ' active' : ''}`}
                    disabled={busy}
                    onClick={() => void chooseRoomRetention(true)}
                  >
                    {en ? 'Retain on every phone' : 'प्रत्येक फ़ोन पर सुरक्षित रखें'}
                  </button>
                  <button
                    type="button"
                    className={`fpp-chip${me?.retainAudio === false ? ' active' : ''}`}
                    disabled={busy}
                    onClick={() => void chooseRoomRetention(false)}
                  >
                    {en ? 'Session-only, do not save' : 'केवल इस सत्र में, सुरक्षित न रखें'}
                  </button>
                </div>
                <div className={`fpp-consent-status${roomRetention === null ? ' pending' : ''}`}>
                  {!enoughOnlinePlayers
                    ? (me?.retainAudio === true
                      ? (en ? 'Your proposal: retain audio. Player 2 must join and accept it.' : 'आपका प्रस्ताव: ध्वनि सुरक्षित रखें। खिलाड़ी २ को जुड़कर इसे स्वीकार करना होगा।')
                      : me?.retainAudio === false
                        ? (en ? 'Your proposal: do not retain audio. Player 2 must join and accept it.' : 'आपका प्रस्ताव: ध्वनि सुरक्षित न रखें। खिलाड़ी २ को जुड़कर इसे स्वीकार करना होगा।')
                        : (en ? 'Choose the retention proposal, then invite player 2.' : 'संग्रह प्रस्ताव चुनें, फिर खिलाड़ी २ को आमंत्रित करें।'))
                    : roomRetention === true
                    ? (en ? 'Agreed: retain audio on every phone.' : 'सहमति: प्रत्येक फ़ोन पर ध्वनि सुरक्षित रहेगी।')
                    : roomRetention === false
                      ? (en ? 'Agreed: keep audio for this session only.' : 'सहमति: ध्वनि केवल इस सत्र में रहेगी।')
                      : (en ? 'Every joined player must select the same option.' : 'प्रत्येक जुड़े खिलाड़ी को एक ही विकल्प चुनना होगा।')}
                </div>
              </fieldset>
              <div className="fpp-row">
                <button
                  type="button"
                  className={`fpp-chip${microphoneReady ? ' active' : ''}`}
                  disabled={busy || !hasRecorder}
                  onClick={() => void enableMicrophone()}
                >
                  {microphoneReady
                    ? (en ? 'Microphone ready' : 'माइक्रोफ़ोन तैयार')
                    : (en ? 'Enable microphone' : 'माइक्रोफ़ोन चालू करें')}
                </button>
                {!allMicrophonesReady && (
                  <small>
                    {en
                      ? 'Every player must enable their microphone on this device.'
                      : 'प्रत्येक खिलाड़ी को अपने उपकरण पर माइक्रोफ़ोन चालू करना होगा।'}
                  </small>
                )}
              </div>
              <p className={audioReady ? 'fpp-audio-ready' : 'fpp-audio-waiting'}>
                {en
                  ? `Audio links: ${connectedPeerCount}/${expectedPeerCount}${audioReady ? ' — ready' : ' — wait before starting.'}`
                  : `ध्वनि-संपर्क: ${connectedPeerCount}/${expectedPeerCount}${audioReady ? ' — तैयार' : ' — शुरू करने से पहले प्रतीक्षा करें।'}`}
              </p>
              {myId === room.hostId && (
                <button type="button" className="fpp-cta" disabled={busy || !enoughOnlinePlayers || roomRetention === null || !audioReady} onClick={() => void handleStartOnline()}>
                  {en ? 'Start match' : 'खेल शुरू करें'}
                </button>
              )}
              {myId !== room.hostId && <p>{en ? 'Waiting for the host to start…' : 'मेज़बान के शुरू करने की प्रतीक्षा…'}</p>}
              </>
              )}
              <button type="button" className="fpp-ghost" onClick={leaveOnlineLobby}>
                {en ? 'Leave room' : 'कक्ष छोड़ें'}
              </button>
            </>
          )}
          {!room && !shareCode && (
            <>
              <div className="fpp-row">
                <span>{en ? 'Local players' : 'स्थानीय खिलाड़ी'}</span>
                {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                  <button key={n} type="button" className={`fpp-chip${playerCount === n ? ' active' : ''}`} onClick={() => setPlayerCount(n)}>{n}</button>
                ))}
              </div>
              <label className="fpp-retain-local">
                <input
                  type="checkbox"
                  checked={localRetainAudio}
                  onChange={(event) => setLocalRetainAudio(event.target.checked)}
                />
                <span>
                  {en
                    ? 'Retain every winning utterance on this device for final review'
                    : 'अंतिम समीक्षा हेतु प्रत्येक विजयी उच्चारण इस उपकरण पर सुरक्षित रखें'}
                </span>
              </label>
              <button type="button" className="fpp-cta" onClick={startLocal}>{en ? 'Start local match' : 'स्थानीय खेल शुरू करें'}</button>
              <button type="button" className="fpp-chip" disabled={busy} onClick={() => void handleCreateRoom()}>
                {en ? 'Create online room' : 'ऑनलाइन कक्ष बनाएँ'}
              </button>
              <div className="fpp-row">
                <input
                  className="fpp-input"
                  value={joinInput}
                  onChange={(e) => setJoinInput(e.target.value.toUpperCase())}
                  placeholder={en ? 'Room code' : 'कक्ष-कोड'}
                  maxLength={6}
                />
                <button type="button" className="fpp-chip" disabled={busy} onClick={() => void handleJoinRoom()}>
                  {en ? 'Join' : 'जुड़ें'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {inPlay && online && !microphoneReady && (
        <div className="fpp-banner fpp-banner-info">
          <p>
            {en
              ? 'This device needs microphone access again before it can send another claim.'
              : 'अगला दावा भेजने से पहले इस उपकरण पर माइक्रोफ़ोन पुनः चालू करें।'}
          </p>
          <button type="button" className="fpp-chip" disabled={busy} onClick={() => void enableMicrophone()}>
            {en ? 'Enable microphone' : 'माइक्रोफ़ोन चालू करें'}
          </button>
        </div>
      )}

      {inPlay && (
        <div className="fpp-table">
          <div className="fpp-zone">
            <div className="fpp-zone-label">{en ? 'Centre' : 'केंद्र'}</div>
            {centerId !== undefined && <FppCardView cardId={centerId} disabled onPick={() => undefined} />}
          </div>
          <div className="fpp-zone">
            {peeking && (
              <div className="fpp-peek-note">
                {en ? `Viewing ${peek?.name} (claims still use your card)` : `${peek?.name} का पत्र (दावा आपके पत्र से होगा)`}
              </div>
            )}
            <div className="fpp-zone-label">
              {peeking
                ? (en ? `${peek?.name}'s card` : `${peek?.name} का पत्र`)
                : (en ? `Your card${me ? ` · ${me.name}` : ''}` : `आपका पत्र${me ? ` · ${me.name}` : ''}`)}
            </div>
            {handId !== undefined && (
              <FppCardView
                cardId={handId}
                shake={shake && !peeking}
                frozen={frozen && !peeking}
                disabled={peeking}
                onPick={pickSymbol}
              />
            )}
          </div>
        </div>
      )}

      {inPlay && (
        <details className="fpp-live-audio" open>
          <summary>
            {en ? `Round audio · ${sessionAudio.length}` : `दौर ध्वनि · ${sessionAudio.length}`}
          </summary>
          {online && Object.entries(remoteStreams).length > 0 && (
            <div className="fpp-remote-audio-list">
              {Object.entries(remoteStreams).map(([remoteId, stream]) => (
                <RemoteAudioPlayer
                  key={remoteId}
                  name={room?.players.find((player) => player.id === remoteId)?.name ?? (en ? 'Teammate' : 'साथी')}
                  stream={stream}
                  en={en}
                />
              ))}
            </div>
          )}
          {sessionAudio.length === 0 ? (
            <p>
              {en
                ? 'Accepted recordings will appear here. Use Play to verify your recording and received teammate audio.'
                : 'स्वीकृत रिकॉर्डिंग यहाँ दिखेंगी। अपनी और साथियों से प्राप्त ध्वनि जाँचने के लिए प्ले दबाएँ।'}
            </p>
          ) : (
            <div className="fpp-live-audio-list">
              {[...sessionAudio].reverse().map((clip) => (
                <div className="fpp-live-audio-item" key={clip.id}>
                  <button
                    type="button"
                    className="fpp-icon-btn"
                    onClick={() => void playUnplayedAudio(clip)}
                    aria-label={en ? `Play ${clip.playerName}'s recording` : `${clip.playerName} की रिकॉर्डिंग सुनें`}
                  >
                    ▶
                  </button>
                  <div>
                    <strong>{clip.playerName} · {clip.word}</strong>
                    <div>{clip.sentence}</div>
                    <small>{deliveryLabel(clip)}</small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </details>
      )}

      {phase === 'win' && (
        <div className="fpp-win">
          <h2>{en ? 'Centre deck empty' : 'केंद्र रिक्त'}</h2>
          <ol>
            {ranked.map((player) => (
              <li key={String(player.id)}>
                {'name' in player ? player.name : (en ? `Player ${player.id}` : `खिलाड़ी ${player.id}`)} — {player.pile.length}
              </li>
            ))}
          </ol>
          {retentionEnabled ? (
            <AudioReview clips={reviewAudio} en={en} onClear={() => void clearReviewAudio()} />
          ) : (
            <p>{en ? 'Players agreed not to retain round audio on their devices.' : 'खिलाड़ियों ने दौर की ध्वनि उपकरणों पर सुरक्षित न रखने का निर्णय लिया था।'}</p>
          )}
          <button
            type="button"
            className="fpp-cta"
            onClick={() => {
              if (room && myId === room.hostId) void closeRoom(room.code);
              setPhase('lobby');
              setMatch(null);
              leaveOnlineLobby();
            }}
          >
            {en ? 'Play again' : 'फिर खेलें'}
          </button>
        </div>
      )}

      {claim && (
        <div className="fpp-overlay" role="dialog" aria-label={en ? 'Claim the card' : 'पत्र का दावा'}>
          <div className="fpp-claim-word">{claim.word}</div>
          <div>{claim.hindi}</div>
          <p className="fpp-speak-instruction">
            {en ? 'Choose a sentence, then record the word followed by the complete sentence.' : 'वाक्य चुनें, फिर शब्द और पूरा वाक्य बोलकर रिकॉर्ड करें।'}
          </p>
          <div className="fpp-frames">
            {sentenceOptions(claim).map((option) => (
              <button
                key={option}
                type="button"
                className={`fpp-frame${frame === option ? ' active' : ''}`}
                onClick={() => setFrame(option)}
              >
                {option}
              </button>
            ))}
          </div>
          {heard && <p><strong>{en ? 'Optional Devanagari transcript' : 'वैकल्पिक देवनागरी लिप्यंतरण'}:</strong> {heard}</p>}
          {audioError && <div className="fpp-banner">{audioError}</div>}
          {!hasRecorder && (
            <div className="fpp-banner">
              {en ? 'This browser cannot record audio. Use a current Chrome, Safari, or Samsung Internet browser.' : 'यह ब्राउज़र ध्वनि रिकॉर्ड नहीं कर सकता। नवीन Chrome, Safari या Samsung Internet प्रयोग करें।'}
            </div>
          )}
          <div className="fpp-row">
            <button
              type="button"
              className={`fpp-cta${recording ? ' recording' : ''}`}
              disabled={!frame || !hasRecorder || processingAudio || (online && !audioReady)}
              onClick={recording ? stopRecording : () => void startRecording()}
            >
              {processingAudio
                ? (en ? 'Preparing audio…' : 'ध्वनि तैयार हो रही है…')
                : recording
                ? (listening ? (en ? 'Listening — stop and send' : 'सुन रहा है — रोकें और भेजें') : (en ? 'Recording — stop and send' : 'रिकॉर्ड हो रहा है — रोकें और भेजें'))
                : (en ? 'Record Sanskrit claim' : 'संस्कृत दावा रिकॉर्ड करें')}
            </button>
            <button type="button" className="fpp-ghost" disabled={recording} onClick={resetClaim}>
              {en ? 'Cancel' : 'रद्द'}
            </button>
          </div>
          <small>
            {canTranscribeDuringRecording
              ? (en ? 'Devanagari transcription is best-effort and never decides the winner.' : 'देवनागरी लिप्यंतरण सहायक मात्र है; उससे विजेता तय नहीं होता।')
              : (en ? 'Your portable audio—not a transcript—is sent to the other players.' : 'अन्य खिलाड़ियों को लिप्यंतरण नहीं, आपकी पोर्टेबल ध्वनि भेजी जाती है।')}
          </small>
        </div>
      )}

      {guideOpen && (
        <div className="fpp-overlay fpp-guide" role="dialog" aria-label={en ? 'One in Nine player guide' : 'एक में नौ मार्गदर्शिका'}>
          <div className="fpp-row">
            <strong>{en ? 'One in Nine · Player guide' : 'एक में नौ · मार्गदर्शिका'}</strong>
            <a className="fpp-ghost" href="/games/amarkosha-fpp/One_in_Nine_FPP_User_Guide.pdf" target="_blank" rel="noreferrer">
              {en ? 'PDF' : 'पीडीएफ'}
            </a>
            <button type="button" className="fpp-ghost" onClick={() => setGuideOpen(false)}>{en ? 'Close' : 'बंद'}</button>
          </div>
          <div className="fpp-tabs">
            <button type="button" className={`fpp-chip${guideTab === 'rules' ? ' active' : ''}`} onClick={() => setGuideTab('rules')}>{en ? 'Objective' : 'उद्देश्य'}</button>
            <button type="button" className={`fpp-chip${guideTab === 'frames' ? ' active' : ''}`} onClick={() => setGuideTab('frames')}>{en ? 'Sentences' : 'वाक्य'}</button>
            <button type="button" className={`fpp-chip${guideTab === 'online' ? ' active' : ''}`} onClick={() => setGuideTab('online')}>{en ? 'Online play' : 'ऑनलाइन'}</button>
            <button type="button" className={`fpp-chip${guideTab === 'guide' ? ' active' : ''}`} onClick={() => setGuideTab('guide')}>{en ? 'Words' : 'शब्द'}</button>
          </div>
          {guideTab === 'rules' && (
            <div className="fpp-guide-copy">
              <p>
                {en
                  ? 'Be first to find the single matching symbol between your card and the centre card, say the Sanskrit word, and speak a complete Sanskrit sentence.'
                  : 'अपने पत्र और केंद्र पत्र के बीच एकमात्र समान चिह्न सबसे पहले ढूँढें, संस्कृत शब्द बोलें, और पूरा संस्कृत वाक्य बोलें।'}
              </p>
              <ol>
                <li>{en ? '73 circular cards; 9 symbols on each. Any two cards share exactly one symbol.' : '७३ वृत्त-पत्र; प्रत्येक पर ९ चिह्न। किन्हीं दो पत्रों पर ठीक एक समान चिह्न होता है।'}</li>
                <li>{en ? 'Tap the shared symbol. A wrong tap pauses you for 1.5 seconds.' : 'समान चिह्न छुएँ। गलत छूने पर १.५ सेकंड रुकना पड़ता है।'}</li>
                <li>{en ? 'Choose a sentence frame, then record the word followed by the sentence.' : 'वाक्य-रचना चुनें, फिर शब्द और पूरा वाक्य रिकॉर्ड करें।'}</li>
                <li>{en ? 'The first accepted audio claim wins the centre card. The game continues at once.' : 'पहला स्वीकृत ध्वनि-दावा केंद्र पत्र जीतता है। खेल तुरन्त चलता रहता है।'}</li>
                <li>{en ? 'When the centre deck is empty, the player with the most cards wins.' : 'केंद्र रिक्त होने पर सबसे अधिक पत्र वाला खिलाड़ी विजयी होता है।'}</li>
              </ol>
              <p>
                {en
                  ? 'Opening this guide never pauses other phones. Your teammates see a book mark on your name.'
                  : 'यह मार्गदर्शिका अन्य फ़ोन नहीं रोकती। साथियों को आपके नाम पर पुस्तक-चिह्न दिखता है।'}
              </p>
            </div>
          )}
          {guideTab === 'frames' && (
            <div className="fpp-guide-copy">
              <p><strong>{en ? 'Identification' : 'पहचान'}</strong></p>
              <p>पुंल्लिङ्ग: एषः सिंहः अस्ति। · स्त्रीलिङ्ग: एषा मक्षिका अस्ति। · नपुंसक: एतत् कमलम् अस्ति।</p>
              <p><strong>{en ? 'Action' : 'क्रिया'}</strong></p>
              <p>{actionSentences('गजः', 'animal').join(' ')}</p>
              <p>{actionSentences('कमलम्', 'plant').join(' ')}</p>
              <p>{actionSentences('दर्पणः', 'object').join(' ')}</p>
              <p><strong>{en ? 'Near-at-hand lion, from Amarakosha' : 'समीप स्थित सिंह — अमरकोश'}</strong></p>
              <ul>
                <li>एषः सिंहः मत्तान् गजान् हन्ति।</li>
                <li>एषः केसरी सटाः विकीर्य तिष्ठति।</li>
                <li>एषः मृगेन्द्रः सर्वान् पशून् अधितिष्ठति।</li>
                <li>एषः पञ्चास्यः गर्जनेन वनं नादयति।</li>
                <li>एषः हरिः समीपतः एव शत्रून् हरति।</li>
                <li>एषः मृगरिपुः हरिणान् प्रति धावति।</li>
                <li>एषः विक्रमी एकाकिनेव समग्रं वनं बिभेति।</li>
              </ul>
            </div>
          )}
          {guideTab === 'online' && (
            <div className="fpp-guide-copy">
              <ol>
                <li>{en ? 'Sign in with different portal accounts on each phone.' : 'प्रत्येक फ़ोन पर अलग पोर्टल खाते से साइन इन करें।'}</li>
                <li>{en ? 'Host taps Create online room, then copies the invite link.' : 'मेज़बान ऑनलाइन कक्ष बनाएँ, फिर आमंत्रण लिंक कॉपी करें।'}</li>
                <li>{en ? 'The guest opens the link and signs in. The same account cannot occupy two seats.' : 'अतिथि लिंक खोलकर साइन इन करें। एक खाता दो आसनों पर नहीं बैठ सकता।'}</li>
                <li>{en ? 'Everyone chooses the same audio-retention option, then Enable microphone.' : 'सभी एक ही ध्वनि-संग्रह विकल्प चुनें, फिर माइक्रोफ़ोन चालू करें।'}</li>
                <li>{en ? 'Wait until Audio links show ready. Only then the host starts the match.' : 'ध्वनि-संपर्क तैयार दिखे, तभी मेज़बान खेल शुरू करें।'}</li>
                <li>{en ? 'Live speech goes phone-to-phone. Winning audio is never uploaded to the server.' : 'प्रत्यक्ष वाणी फ़ोन-से-फ़ोन जाती है। विजयी ध्वनि सर्वर पर नहीं जाती।'}</li>
              </ol>
              <p>
                {en
                  ? 'If a link drops, recording is blocked until it reconnects. Status appears at the top of this page.'
                  : 'संपर्क टूटने पर जुड़ने तक रिकॉर्ड बंद रहता है। स्थिति इस पृष्ठ के शीर्ष पर दिखती है।'}
              </p>
            </div>
          )}
          {guideTab === 'guide' && (
            <>
              <input
                className="fpp-input fpp-search"
                value={guideQuery}
                onChange={(e) => setGuideQuery(e.target.value)}
                placeholder={en ? 'Search words, Hindi, dhātu…' : 'शब्द, हिंदी, धातु खोजें…'}
              />
              {guideSymbols.slice(0, 73).map((item) => (
                <div key={item.word} className="fpp-guide-item">
                  <img src={item.symbol} alt="" />
                  <div>
                    <strong>{item.word}</strong> · {item.hindi}
                    <div>{item.gender === 'f' ? 'स्त्रीलिङ्ग' : item.gender === 'n' ? 'नपुंसकलिङ्ग' : 'पुंल्लिङ्ग'} · {item.dhatu} — {item.dhatu_artha}</div>
                    <small>{item.nirukta}</small>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {unplayedAudio.length > 0 && (
        <div className="fpp-unplayed" role="status">
          <strong>{en ? 'Received audio awaiting playback' : 'प्राप्त ध्वनि सुनना शेष'}</strong>
          {unplayedAudio.map((clip) => (
            <button key={clip.id} type="button" className="fpp-chip" onClick={() => void playUnplayedAudio(clip)}>
              ▶ {clip.playerName} · {clip.word}
            </button>
          ))}
        </div>
      )}
      {toast && (
        <div className={`fpp-toast fpp-toast-${toast.kind}`} role="status" aria-live="polite">
          {toast.text}
        </div>
      )}
    </div>
  );
}
